import * as React from 'react';
import { useSlate } from 'slate-react';
import styled from 'styled-components';

import { TextButton } from '@xing-com/button';
import { IconArrowDown, IconArrowUp } from '@xing-com/icons';
import { mediaTiny, mediaSmallOrTiny } from '@xing-com/layout-tokens';
import {
  spaceM,
  spaceXL,
  xdlPaletteGrayPlain03,
  xdlPaletteGrayPlain04,
} from '@xing-com/tokens';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';

import { ToolbarButton } from './ToolbarButton';

const ToolbarComponent = styled.div`
  display: flex;
  justify-content: center;
  overflow: hidden;

  @media ${mediaSmallOrTiny} {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow: hidden;
    padding: ${spaceM} 0;
  }

  @media ${mediaTiny} {
    justify-content: space-between;
  }

  @media only screen and (max-width: 335px) {
    margin: 0 -8px;
  }
`;

const ToolbarContainer = styled.div`
  display: flex;
  flex-direction: row;

  @media ${mediaSmallOrTiny} {
    flex-wrap: wrap;
    height: ${(props: ToolbarContainerProps) =>
      props.collapsed === false ? '42px' : 'auto'};
    overflow: hidden;
    align-items: center;
  }
`;

const ButtonGroup = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 66px;
  padding: ${spaceM} ${spaceXL} ${spaceM} 0;

  &:only-child {
    padding-right: 0;
  }
`;

const ToggleButton = styled(TextButton)`
  border-radius: 50px;
  display: none;
  background: ${(props) =>
    props.disabled === undefined
      ? xdlPaletteGrayPlain04
      : xdlPaletteGrayPlain03};

  &:hover,
  &:focus {
    background: ${(props) =>
      props.disabled === undefined
        ? xdlPaletteGrayPlain04
        : xdlPaletteGrayPlain03};
  }

  @media (max-width: 631px) {
    display: flex;
  }
`;

type ToolbarProps = {
  buttons: Array<any>;
  className?: string;
};

type ToolbarContainerProps = {
  collapsed: boolean;
};

const Toolbar = ({ buttons = [], className }: ToolbarProps) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const editor = useSlate();
  const breakpoint = useMediaListener();

  return (
    <ToolbarComponent className={className}>
      <ToolbarContainer collapsed={collapsed}>
        {buttons.map((group, groupIndex) => {
          if (group.length > 0) {
            return breakpoint === BREAKPOINTS.handheld ? (
              // @ts-expect-error Leftover after Brewery migration
              group.map((button, index) => (
                <ToolbarButton button={button} key={index} />
              ))
            ) : (
              <ButtonGroup key={groupIndex}>
                {/* @ts-expect-error Leftover after Brewery migration */}
                {group.map((button, index) => (
                  <ToolbarButton button={button} key={index} />
                ))}
              </ButtonGroup>
            );
          }
          return undefined;
        })}
      </ToolbarContainer>
      <ToggleButton
        disabled={editor.selection === null}
        icon={collapsed === false ? IconArrowDown : IconArrowUp}
        onClick={() => setCollapsed(!collapsed)}
        size="medium"
      ></ToggleButton>
    </ToolbarComponent>
  );
};

export { Toolbar };
